import { useCallback, useState, useRef, useEffect } from 'react';
import { browserControlWS } from '../services/browserControlWebSocket';
import { withSelectedLanguage } from '../i18n/selectedLanguage';
import { getCurrentPageContext } from '../utils/pageContextParser'; // Issue #409
import { config } from '../config/runtimeConfig'; // Issue #880

/**
 * #1485: which instance of this hook on the page sent which request (message id → owner).
 *
 * Every instance — the floating widget's chat, a form chat beside it — gets the frames of the
 * page's one WebSocket. b2b-api hands an answer whose asking socket closed to the session's next
 * socket, and each handed-over frame names its request (`requestMessageId`):
 * - the instance that sent the request continues that request's own bubble;
 * - the other instances leave it alone;
 * - an answer to a request no instance here sent (the page before a reload) is shown as a new
 *   message.
 */
const requestOwners = new Map();

/**
 * Хук для текстового чата через WebSocket (browser-control)
 * Используется когда LiveKit WebRTC соединение не установлено
 *
 * @param {Object} options
 * @param {string} options.sessionId - ID сессии для сохранения контекста
 * @param {number} options.textChatVersion - Issue #496: Счётчик версии для переподписки при смене режима voice/text
 * @param {string} options.workflowType - Тип workflow (MAIN_WORKFLOW, AUTOHEAVEN_WORKFLOW, ANDYS_WORKFLOW)
 * @param {string} options.inventoryId - ID инвентаря (опционально)
 * @param {function} options.onMessage - Callback для получения streaming сообщений (content, accumulatedContent)
 * @param {function} options.onError - Callback для обработки ошибок
 * @param {function} options.onComplete - Callback при завершении streaming
 * @param {function} options.onStreamStart - Callback при начале streaming
 * @param {function} options.onProactiveStart - Issue #324: Callback при начале proactive response (после tool completion):
 *   (runId, requestMessageId). #1485: requestMessageId — запрос, на который отвечает переданный ответ
 * @param {function} options.onProactiveChunk - Issue #324: Callback для чанков proactive response (content, accumulatedContent)
 * @param {function} options.onProactiveDone - Issue #324: Callback при завершении proactive response
 * @param {function} options.onSuggestions - Issue #752: Callback для авто-предложений (suggestions[], messageId)
 * @param {boolean} options.allowProactiveBeforeSession - #1485: принимать proactive-ответ без
 *   `requestMessageId`, пока sessionId ещё не задан. Только для страницы, которую перезагрузила
 *   навигация агента. Переданный ответ (с `requestMessageId`) принимается и так — см. requestOwners.
 */
const useTextChat = ({
  sessionId,
  textChatVersion = 0,
  workflowType = 'MAIN_WORKFLOW',
  inventoryId,
  onMessage,
  onError,
  onComplete,
  onStreamStart,
  onProactiveStart,
  onProactiveChunk,
  onProactiveDone,
  onSuggestions,
  allowProactiveBeforeSession = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Текущий messageId для отслеживания ответа
  const currentMessageIdRef = useRef(null);

  // Накопленный контент для текущего сообщения
  const accumulatedContentRef = useRef('');

  // Issue #324: Накопленный контент для proactive response
  const proactiveContentRef = useRef('');
  const proactiveRunIdRef = useRef(null);

  // #1485: the socket generation each stream started on (browserControlWS.getSocketGeneration)
  const requestSocketGenRef = useRef(0);
  const proactiveSocketGenRef = useRef(0);
  // #1485: the request as sent, and whether b2b-api acknowledged it (chat-stream-start), so a
  // request lost with its socket before it reached b2b-api can be sent again on the next one —
  // with the same message id, which b2b-api recognises if the first copy did arrive.
  const lastRequestRef = useRef(null);
  const requestStartedRef = useRef(false);
  // #1485: this instance in requestOwners, and the request each proactive bubble answers.
  const ownerRef = useRef(Symbol('useTextChat'));
  const proactiveRequestIdRef = useRef(null);

  /** #1485: the request is over here; another instance may now show a late frame of it. */
  const releaseRequest = useCallback((messageId) => {
    if (messageId && requestOwners.get(messageId) === ownerRef.current) {
      requestOwners.delete(messageId);
    }
  }, []);

  useEffect(() => {
    const owner = ownerRef.current;
    return () => {
      for (const [messageId, by] of requestOwners) {
        if (by === owner) requestOwners.delete(messageId);
      }
    };
  }, []);

  /**
   * #1485: frames of this instance's own request, handed over to its new socket by b2b-api after
   * the old one closed. They continue the request's bubble: chunks go to onMessage, the end to
   * onComplete, or to onError when the answer failed.
   */
  const continueRequest = useCallback((data) => {
    if (data.chunkType === 'start') {
      // The request goes on, so it is not lost with the old socket (the 'registered' handler).
      requestSocketGenRef.current = browserControlWS.getSocketGeneration();
      requestStartedRef.current = true;
    } else if (data.chunkType === 'chunk' && data.content) {
      accumulatedContentRef.current += data.content;
      onMessage?.(data.content, accumulatedContentRef.current);
    } else if (data.chunkType === 'done' || data.chunkType === 'error') {
      const messageId = currentMessageIdRef.current;
      setIsLoading(false);
      releaseRequest(messageId);
      currentMessageIdRef.current = null;
      if (data.error || data.chunkType === 'error') {
        const err = new Error(data.error || 'The answer was interrupted');
        setError(err);
        onError?.(err);
      } else {
        onComplete?.(accumulatedContentRef.current);
        if (Array.isArray(data.suggestions) && data.suggestions.length > 0) {
          onSuggestions?.(data.suggestions, messageId);
        }
      }
    }
  }, [onMessage, onComplete, onError, onSuggestions, releaseRequest]);

  /**
   * Обработчик входящих сообщений от WebSocket
   */
  const handleWebSocketMessage = useCallback((data) => {
    // Проверяем, что сообщение относится к текущему запросу
    // Issue #543: Skip messageId check for proactive messages (they don't have messageId)
    if (data.type !== 'proactive-stream-chunk' &&
        currentMessageIdRef.current && data.messageId !== currentMessageIdRef.current) {
      return;
    }

    switch (data.type) {
      case 'chat-stream-start':
        console.log('[useTextChat] Stream started:', data.messageId);
        requestStartedRef.current = true;
        accumulatedContentRef.current = '';
        onStreamStart?.();
        break;

      case 'chat-stream-chunk':
        // Получили новый chunk контента
        if (data.content) {
          accumulatedContentRef.current = data.accumulatedContent || accumulatedContentRef.current + data.content;
          onMessage?.(data.content, accumulatedContentRef.current);
        }
        break;

      case 'chat-stream-end':
        console.log('[useTextChat] Stream completed, length:', data.content?.length);
        setIsLoading(false);
        releaseRequest(currentMessageIdRef.current);
        currentMessageIdRef.current = null;
        onComplete?.(data.content || accumulatedContentRef.current);
        // Issue #752: Forward suggestions if present
        if (Array.isArray(data.suggestions) && data.suggestions.length > 0) {
          onSuggestions?.(data.suggestions, data.messageId);
        }
        break;

      case 'chat-stream-error':
        console.error('[useTextChat] Stream error:', data.error);
        setIsLoading(false);
        setError(new Error(data.error));
        releaseRequest(currentMessageIdRef.current);
        currentMessageIdRef.current = null;
        onError?.(new Error(data.error));
        break;

      case 'chat-error':
        console.error('[useTextChat] Chat error:', data.error);
        setIsLoading(false);
        setError(new Error(data.error));
        releaseRequest(currentMessageIdRef.current);
        currentMessageIdRef.current = null;
        onError?.(new Error(data.error));
        break;

      // Issue #324: Proactive response stream (after async tool completion)
      case 'proactive-stream-chunk': {
        // #1485: a handed-over answer names its request (see requestOwners).
        const requestId = data.requestMessageId || null;
        const owner = requestId ? requestOwners.get(requestId) : undefined;
        if (owner === ownerRef.current) {
          // The rest of this instance's own request, after its socket was replaced: it goes on
          // in the request's bubble, above anything typed meanwhile.
          if (requestId === currentMessageIdRef.current) continueRequest(data);
          return;
        }
        if (owner !== undefined) return; // another chat on this page asked it

        // Проверяем sessionId если он есть в data. #1485: a handed-over answer to a request no
        // chat on this page sent belongs to this page's session — b2b-api sends it to the socket
        // registered for it — also before the page has set its sessionId (a page reloaded by
        // the agent's navigation, or by the customer). Other proactive frames are taken then only
        // on a page the agent's navigation reloaded.
        if (data.sessionId && (sessionId ? data.sessionId !== sessionId : !(requestId || allowProactiveBeforeSession))) {
          return;
        }

        // Issue #497: Log all proactive events for debugging
        console.log('[useTextChat][Issue #497] PROACTIVE_RECEIVED:', {
          sessionId: data.sessionId,
          chunkType: data.chunkType,
          runId: data.runId,
          contentLength: data.content?.length || 0,
          timestamp: Date.now(),
        });

        if (data.chunkType === 'start') {
          console.log('[useTextChat][Issue #497] PROACTIVE_START:', data.runId);
          if (requestId && proactiveRunIdRef.current && proactiveRequestIdRef.current === requestId) {
            // #1485: the same handed-over answer, taken over again after this page reconnected:
            // its bubble goes on.
            proactiveRunIdRef.current = data.runId;
            proactiveSocketGenRef.current = browserControlWS.getSocketGeneration();
            break;
          }
          if (proactiveRunIdRef.current) {
            // #1485: a new answer while another is still open — that one is over.
            onProactiveDone?.(proactiveContentRef.current);
          }
          proactiveContentRef.current = '';
          proactiveRunIdRef.current = data.runId;
          proactiveRequestIdRef.current = requestId;
          proactiveSocketGenRef.current = browserControlWS.getSocketGeneration();
          console.log('[useTextChat][Issue #497] CALLBACK_onProactiveStart called');
          onProactiveStart?.(data.runId, requestId);
        } else if (data.chunkType === 'chunk' && data.content) {
          console.log('[useTextChat][Issue #497] PROACTIVE_CHUNK:', {
            runId: data.runId,
            contentLength: data.content.length,
            accumulatedLength: proactiveContentRef.current.length + data.content.length,
          });
          proactiveContentRef.current += data.content;
          console.log('[useTextChat][Issue #497] CALLBACK_onProactiveChunk called, length:', data.content.length);
          onProactiveChunk?.(data.content, proactiveContentRef.current);
        } else if (data.chunkType === 'done') {
          console.log('[useTextChat][Issue #497] PROACTIVE_DONE:', {
            runId: data.runId,
            totalLength: proactiveContentRef.current.length,
          });
          console.log('[useTextChat][Issue #497] CALLBACK_onProactiveDone called, totalLength:', proactiveContentRef.current.length);
          onProactiveDone?.(proactiveContentRef.current);
          // Issue #752: Forward suggestions if present in proactive done
          if (Array.isArray(data.suggestions) && data.suggestions.length > 0) {
            onSuggestions?.(data.suggestions, data.runId);
          }
          proactiveRunIdRef.current = null;
          proactiveRequestIdRef.current = null;
        } else if (data.chunkType === 'error') {
          console.error('[useTextChat][Issue #497] PROACTIVE_ERROR:', data.error);
          onError?.(new Error(data.error || 'Proactive response error'));
          proactiveRunIdRef.current = null;
          proactiveRequestIdRef.current = null;
        }
        break;
      }
    }
  }, [sessionId, onMessage, onError, onComplete, onStreamStart, onProactiveStart, onProactiveChunk, onProactiveDone, onSuggestions, allowProactiveBeforeSession, continueRequest, releaseRequest]);

  /**
   * Подписка на WebSocket сообщения
   * Issue #496: Добавлен textChatVersion в зависимости чтобы переподписаться при переключении режимов voice/text
   */
  useEffect(() => {
    // Подписываемся на все типы chat сообщений
    const messageTypes = [
      'chat-stream-start',
      'chat-stream-chunk',
      'chat-stream-end',
      'chat-stream-error',
      'chat-error',
      // Issue #324: Proactive response stream (after async tool completion)
      'proactive-stream-chunk',
    ];

    // Issue #496: Логируем переподписку для отладки
    console.log('[useTextChat] Subscribing to WebSocket messages, textChatVersion:', textChatVersion);

    messageTypes.forEach(type => {
      browserControlWS.onMessage(type, handleWebSocketMessage);
    });

    return () => {
      // Отписываемся при unmount или изменении зависимостей
      console.log('[useTextChat] Unsubscribing from WebSocket messages');
      messageTypes.forEach(type => {
        browserControlWS.offMessage(type, handleWebSocketMessage);
      });
    };
  }, [handleWebSocketMessage, textChatVersion]);

  // Реактивное состояние доступности WebSocket
  const [isApiAvailable, setIsApiAvailable] = useState(false);

  /**
   * Подписка на изменения состояния WebSocket соединения
   * Реактивно обновляет isApiAvailable при connect/disconnect
   */
  useEffect(() => {
    const handleConnectionChange = () => {
      setIsApiAvailable(browserControlWS.isConnected());
    };

    // Начальное состояние
    setIsApiAvailable(browserControlWS.isConnected());

    // Подписка на события соединения
    browserControlWS.onMessage('connected', handleConnectionChange);
    browserControlWS.onMessage('registered', handleConnectionChange);

    // Также слушаем close через window event (browserControlWS не emit'ит disconnect напрямую)
    const handleDisconnect = () => {
      setIsApiAvailable(false);
    };

    // Проверяем состояние периодически (fallback если события не пришли)
    const intervalId = setInterval(() => {
      const connected = browserControlWS.isConnected();
      setIsApiAvailable(prev => prev !== connected ? connected : prev);
    }, 1000);

    return () => {
      browserControlWS.offMessage('connected', handleConnectionChange);
      browserControlWS.offMessage('registered', handleConnectionChange);
      clearInterval(intervalId);
    };
  }, []);

  /**
   * #1485: a stream started on a socket that has since closed will not finish on this page —
   * b2b-api hands the rest of that answer over as a proactive message instead. On the new
   * socket's 'registered', end such a stream here with what it showed, so the chat is not left
   * "streaming" with the customer's next messages queued behind it (T85's e2e: 95 s stuck).
   * A request b2b-api never acknowledged most likely never reached it: that one is sent again.
   *
   * Re-subscribed whenever the text chat is re-activated (textChatVersion):
   * browserControlWS.disconnect() — the end of a voice call — drops every handler.
   */
  useEffect(() => {
    const handleRegistered = () => {
      const generation = browserControlWS.getSocketGeneration();
      if (currentMessageIdRef.current && requestSocketGenRef.current !== generation) {
        const lost = lastRequestRef.current;
        const resentId =
          !requestStartedRef.current && lost
            ? browserControlWS.sendChatMessage(lost.message, {
                ...lost.payload,
                messageId: lost.messageId,
              })
            : null;
        if (resentId) {
          console.warn('[useTextChat][#1485] Request lost before b2b-api got it, sent again:', resentId);
          currentMessageIdRef.current = resentId;
          requestSocketGenRef.current = generation;
        } else {
          console.warn('[useTextChat][#1485] Stream lost with its socket:', currentMessageIdRef.current);
          releaseRequest(currentMessageIdRef.current);
          currentMessageIdRef.current = null;
          setIsLoading(false);
          onComplete?.(accumulatedContentRef.current);
          accumulatedContentRef.current = '';
        }
      }
      if (proactiveRunIdRef.current && proactiveSocketGenRef.current !== generation) {
        console.warn('[useTextChat][#1485] Proactive stream lost with its socket:', proactiveRunIdRef.current);
        proactiveRunIdRef.current = null;
        onProactiveDone?.(proactiveContentRef.current);
        proactiveContentRef.current = '';
      }
    };

    browserControlWS.onMessage('registered', handleRegistered);
    return () => {
      browserControlWS.offMessage('registered', handleRegistered);
    };
  }, [onComplete, onProactiveDone, textChatVersion, releaseRequest]);

  /**
   * Проверить доступность WebSocket (instant check, не реактивный)
   */
  const isWebSocketAvailable = useCallback(() => {
    return browserControlWS.isConnected();
  }, []);

  /**
   * Отправить сообщение через WebSocket
   *
   * @param {string} message - Текст сообщения (может быть пустым при launchContext)
   * @param {Object} options - Дополнительные опции
   * @param {Object} options.launchContext - Issue #461: Контекст запуска для auto-greeting
   * @returns {Promise<void>}
   */
  const sendMessage = useCallback(async (message, options = {}) => {
    const hasLaunchContext = options.launchContext && typeof options.launchContext === 'object';

    // Issue #461: Разрешаем пустые сообщения если есть launchContext (для auto-greeting)
    if ((!message || message.trim().length === 0) && !hasLaunchContext) {
      console.warn('[useTextChat] Empty message without launchContext, skipping');
      return;
    }

    if (!isWebSocketAvailable()) {
      const err = new Error('WebSocket is not connected');
      setError(err);
      onError?.(err);
      return;
    }

    // Issue #461: Fallback на browserControlWS.sessionId если React state ещё не обновился
    if (!sessionId && !browserControlWS.sessionId) {
      const err = new Error('Session ID is required');
      setError(err);
      onError?.(err);
      return;
    }

    // Если предыдущий запрос еще в процессе, отменяем его
    // Сбрасываем messageId чтобы игнорировать ответы от старого запроса
    if (currentMessageIdRef.current) {
      console.warn('[useTextChat] Aborting previous request:', currentMessageIdRef.current);
      currentMessageIdRef.current = null;
    }

    setIsLoading(true);
    setError(null);
    accumulatedContentRef.current = '';

    // Issue #587: Diagnostic log with stack trace to find duplicate request sources
    const _launchAction = options.launchContext?.action || null;
    const _caller = new Error().stack?.split('\n').slice(1, 4).map(l => l.trim()).join(' <- ') || 'unknown';
    console.warn('[useTextChat][#587-DIAG] sendMessage CALLED:', {
      sessionId,
      workflowType,
      messageLength: message ? message.length : 0,
      hasLaunchContext,
      launchAction: _launchAction,
      timestamp: Date.now(),
      caller: _caller,
    });

    // Issue #409: Отправляем сообщение через WebSocket с контекстом страницы
    // Issue #461: Добавляем launchContext for auto-greeting
    // Issue #886: Nested `suggestions` cfg (replaces flat #880 boolean) —
    // mapping `widgetConfig.suggestions.{enabled, maxVisible}` →
    // `payload.suggestions.{enabled, params: {max_suggestions}}`. The server
    // ignores accentColor/bubbleVariant — those are pure visual.
    const suggestionsEnabled = config.SUGGESTIONS_ENABLED;
    const maxVisible = config.SUGGESTIONS_MAX_VISIBLE;
    const suggestionsCfg = {
      enabled: !!suggestionsEnabled,
      ...(typeof maxVisible === 'number' && maxVisible > 0
        ? { params: { max_suggestions: maxVisible } }
        : {}),
    };
    const payload = {
      workflowType,
      inventoryId,
      // #1487: the chat message carries the listing of the car on screen; the agent
      // builds its car block from it for this turn.
      pageContext: getCurrentPageContext({ withCarListing: true }),
      // #1362: ЕДИНСТВЕННАЯ точка, где выбранный язык витрины попадает в
      // текстовый launch context. Раньше его клали руками две CTA лендинга, и
      // остальные входы (пузырь виджета, Sparkles, SDK open/toggle/set-mode,
      // авто-старт после навигации, form→chat handover из FormWidgetChat со
      // СВОИМ экземпляром этого хука) язык теряли — агент здоровался
      // по-английски на немецкой странице. Здесь сходятся все: это
      // единственный вызывающий `browserControlWS.sendChatMessage` во всём
      // приложении, поэтому новая точка входа не может потерять язык заново.
      launchContext: withSelectedLanguage(options.launchContext), // Issue #461, #1362
      suggestions: suggestionsCfg, // Issue #886
    };
    const messageId = browserControlWS.sendChatMessage(message || '', payload);

    if (!messageId) {
      const err = new Error('Failed to send chat message');
      setIsLoading(false);
      setError(err);
      onError?.(err);
      return;
    }

    currentMessageIdRef.current = messageId;
    requestOwners.set(messageId, ownerRef.current); // #1485
    // #1485: see the 'registered' handler above
    requestSocketGenRef.current = browserControlWS.getSocketGeneration();
    lastRequestRef.current = { message: message || '', payload, messageId };
    requestStartedRef.current = false;
    console.warn('[useTextChat][#587-DIAG] Message SENT via WS, messageId:', messageId, 'action:', _launchAction);
  }, [sessionId, workflowType, inventoryId, isWebSocketAvailable, onError]);

  /**
   * Отменить текущий запрос
   * Примечание: WebSocket не поддерживает отмену запроса напрямую,
   * но мы можем игнорировать ответы
   */
  const abort = useCallback(() => {
    if (currentMessageIdRef.current) {
      console.log('[useTextChat] Aborting request:', currentMessageIdRef.current);
      releaseRequest(currentMessageIdRef.current);
      currentMessageIdRef.current = null;
      setIsLoading(false);
    }
  }, [releaseRequest]);

  return {
    sendMessage,
    abort,
    isLoading,
    error,
    isApiAvailable,
  };
};

export default useTextChat;
